import type { ComponentType } from 'react';
import {
  IconAt,
  IconCircleCheck,
  IconCircleExclamation,
  IconComment,
  IconFaceSmile,
  IconReply,
  IconUserPlus,
} from '@optimizely/axiom-icons';

import type { NotificationCategory, NotificationPriority, NotificationType } from '../types';

/**
 * Inputs that can describe a notification — covers both the API model
 * (`Notification`) and the websocket payload (`NotificationCreatedData`).
 */
type NotificationLike = {
  type: NotificationType | string;
  metadata?: {
    thread_id?: string;
    conversation_id?: string;
    message_id?: string;
    execution_id?: string;
    trigger_type?: string;
    trigger_id?: string | null;
  } | null;
};

type DeepLinkBuilder = (notification: NotificationLike) => string;

/**
 * Toast/Indicator intents in Axiom. Notifications fall back to `information`
 * when a type isn't registered.
 */
export type NotificationIntent = 'success' | 'danger' | 'information';

/**
 * The single source of truth for everything a notification type drives in the
 * UI: which icon and color it uses, what toast intent fires, how it deep-links,
 * and its priority/category metadata. Add a new notification type here and
 * every consumer (tray, table cell, toast, badge) picks it up automatically.
 */
type NotificationVisuals = {
  Icon: ComponentType;
  color: 'fg.error' | 'fg.success' | 'fg.default';
  intent: NotificationIntent;
  priority: NotificationPriority;
  category: NotificationCategory;
  buildDeepLinkPath: DeepLinkBuilder;
};

const chatThreadLink: DeepLinkBuilder = notification =>
  `/chat?threadId=${notification.metadata?.thread_id ?? ''}`;

const workflowExecutionLink: DeepLinkBuilder = notification =>
  `/agents/workflow/executions/${notification.metadata?.execution_id ?? ''}`;

const messagingConversationLink: DeepLinkBuilder = notification => {
  const meta = notification.metadata as {
    conversation_id?: string;
    message_id?: string;
    thread_id?: string;
  } | null;
  const params = new URLSearchParams();
  params.set('tab', 'messages');
  if (meta?.conversation_id) params.set('conversationId', meta.conversation_id);
  if (meta?.message_id) params.set('messageId', meta.message_id);
  if (meta?.thread_id) params.set('msgThreadId', meta.thread_id);
  return `/chat?${params.toString()}`;
};

const NOTIFICATION_VISUALS: Record<NotificationType, NotificationVisuals> = {
  chat_agent_execution_completed: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Agent Execution',
    buildDeepLinkPath: chatThreadLink,
  },
  chat_agent_execution_failed: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Agent Execution',
    buildDeepLinkPath: chatThreadLink,
  },
  autonomous_workflow_execution_completed: {
    Icon: IconCircleCheck,
    color: 'fg.success',
    intent: 'success',
    priority: 'Low',
    category: 'Agent Execution',
    buildDeepLinkPath: workflowExecutionLink,
  },
  autonomous_workflow_execution_failed: {
    Icon: IconCircleExclamation,
    color: 'fg.error',
    intent: 'danger',
    priority: 'High',
    category: 'Agent Execution',
    buildDeepLinkPath: workflowExecutionLink,
  },
  messaging_new_message: {
    Icon: IconComment,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_mention: {
    Icon: IconAt,
    color: 'fg.default',
    intent: 'information',
    priority: 'High',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_reply: {
    Icon: IconReply,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_reaction: {
    Icon: IconFaceSmile,
    color: 'fg.default',
    intent: 'information',
    priority: 'Low',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_channel_invite: {
    Icon: IconUserPlus,
    color: 'fg.success',
    intent: 'success',
    priority: 'High',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
  messaging_group_invite: {
    Icon: IconUserPlus,
    color: 'fg.success',
    intent: 'success',
    priority: 'High',
    category: 'Messaging',
    buildDeepLinkPath: messagingConversationLink,
  },
};

const FALLBACK_DEEP_LINK_PATH = '/notifications';

export function getNotificationVisuals(
  type: NotificationType | string
): NotificationVisuals | undefined {
  return NOTIFICATION_VISUALS[type as NotificationType];
}

export function getPriority(type: NotificationType): NotificationPriority {
  return NOTIFICATION_VISUALS[type]?.priority ?? 'Low';
}

export function getCategory(type: NotificationType): NotificationCategory {
  return NOTIFICATION_VISUALS[type]?.category ?? 'Agent Execution';
}

export function getNotificationIntent(type: NotificationType | string): NotificationIntent {
  return NOTIFICATION_VISUALS[type as NotificationType]?.intent ?? 'information';
}

/**
 * Build the in-app deep-link target for a notification, relative to the
 * current instance namespace (use with `useNamespacedNavigate` or prefix
 * with `/instances/{instanceId}` for an `<a href>`).
 *
 * Falls back to the notification center when the type isn't registered.
 */
export function getDeepLinkPath(notification: NotificationLike): string {
  return (
    NOTIFICATION_VISUALS[notification.type as NotificationType]?.buildDeepLinkPath(notification) ??
    FALLBACK_DEEP_LINK_PATH
  );
}

/**
 * Formats an unread count for display in a badge or tab label.
 * - 0 / undefined → ''
 * - 1–99 → the number
 * - 100+ → '99+'
 */
export function formatUnreadCount(count: number | undefined): string {
  if (!count || count <= 0) return '';
  return count > 99 ? '99+' : String(count);
}

// --- Filter options (shared by NotificationFilterBar) ---

/** Backend category_key values (matches `CATEGORY_TYPE_MAP` in notification-service registry). */
export type NotificationCategoryFilter = 'agent_execution' | 'messaging';

/** Backend priority values (matches `PRIORITY_TYPE_MAP` in notification-service registry). */
export type NotificationPriorityFilter = 'critical' | 'high' | 'medium' | 'low';

export const CATEGORY_OPTIONS: ReadonlyArray<{
  label: string;
  value: NotificationCategoryFilter;
}> = [
  { label: 'Agent Execution', value: 'agent_execution' },
  { label: 'Messaging', value: 'messaging' },
];

export const PRIORITY_OPTIONS: ReadonlyArray<{
  label: string;
  value: NotificationPriorityFilter;
}> = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

export type TimePreset = 'last_hour' | 'last_24_hours' | 'last_7_days' | 'last_30_days';

export const TIME_PRESETS: ReadonlyArray<{ label: string; value: TimePreset }> = [
  { label: 'Last Hour', value: 'last_hour' },
  { label: 'Last 24 Hours', value: 'last_24_hours' },
  { label: 'Last 7 Days', value: 'last_7_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
];

const TIME_PRESET_MS: Record<TimePreset, number> = {
  last_hour: 3_600_000,
  last_24_hours: 86_400_000,
  last_7_days: 7 * 86_400_000,
  last_30_days: 30 * 86_400_000,
};

/**
 * Type guard: narrows `string | null | undefined` to `TimePreset` if the
 * value matches a known preset.
 */
export function isTimePreset(value: string | null | undefined): value is TimePreset {
  return value !== null && value !== undefined && value in TIME_PRESET_MS;
}

/**
 * Converts a time preset to the ISO datetime to send as `created_after`.
 * Each preset is a sliding window relative to `now`.
 */
export function timePresetToCreatedAfter(preset: TimePreset, now: Date = new Date()): string {
  return new Date(now.getTime() - TIME_PRESET_MS[preset]).toISOString();
}

export function formatTimestamp(date: Date): { time: string; date: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (dateStart.getTime() === today.getTime()) {
    return { time, date: 'Today' };
  }
  if (dateStart.getTime() === yesterday.getTime()) {
    return { time, date: 'Yesterday' };
  }
  return {
    time,
    date: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  };
}
